/**
 * boBundleBlob.ts
 *
 * Resolves the URL for bo_bundle ZIP files from Azure Blob Storage.
 * Supports:
 * 1. Current format (with OHLC encoded in filename, generated by 2i_bo-replay):
 *    e.g. bo_bundle/{YYYYMMDD}/{STOCK}_part1_o{open}_h{high}_l{low}_c{close}_p{prev}.zip
 *         bo_bundle/{YYYYMMDD}/{STOCK}_o{open}_h{high}_l{low}_c{close}_p{prev}.zip
 * 2. Legacy / simple format (without OHLC suffix):
 *    e.g. bo_bundle/{YYYYMMDD}/{STOCK}_part1.zip
 *         bo_bundle/{YYYYMMDD}/{STOCK}.zip
 */

export interface ParsedOhlc {
  open: number;
  high: number;
  low: number;
  close: number;
  prevClose: number;
}

export interface BoBundleResolution {
  part1ZipUrl: string | null;
  fullZipUrl: string | null;
  parsedOhlc: ParsedOhlc | null;
}

export async function resolveBoBundleUrls(
  containerUrl: string,
  sasToken: string,
  dateFormatted: string,
  stock: string,
  signal?: AbortSignal
): Promise<BoBundleResolution> {
  const isDev = (import.meta as any).env?.DEV || false;
  const prefix = `bo_bundle/${dateFormatted}/${stock}`;
  const listUrl = isDev
    ? `/azure-blob-proxy/trading-data-v2?restype=container&comp=list&prefix=${prefix}&${sasToken}`
    : `${containerUrl}?restype=container&comp=list&prefix=${prefix}&${sasToken}`;

  const defaultPart1Path = `bo_bundle/${dateFormatted}/${stock}_part1.zip`;
  const defaultFullPath = `bo_bundle/${dateFormatted}/${stock}.zip`;

  const makeUrl = (path: string) =>
    isDev
      ? `/azure-blob-proxy/trading-data-v2/${path}?${sasToken}`
      : `${containerUrl}/${path}?${sasToken}`;

  try {
    const res = await fetch(listUrl, { signal, priority: 'high' } as RequestInit);
    if (res.ok) {
      const xml = await res.text();
      const blobNames: string[] = [];
      const regex = /<Name>([^<]+)<\/Name>/g;
      let match;
      while ((match = regex.exec(xml)) !== null) {
        if (match[1]) blobNames.push(match[1]);
      }

      if (blobNames.length > 0) {
        let part1Path: string | null = null;
        let fullPath: string | null = null;
        let parsedOhlc: ParsedOhlc | null = null;

        for (const name of blobNames) {
          if (!name.endsWith('.zip')) continue;
          const fileName = name.split('/').pop() || '';

          // Validate that the filename specifically starts with stock ticker
          const isValidPrefix = fileName === `${stock}.zip` || fileName.startsWith(`${stock}_`);
          if (!isValidPrefix) continue;

          if (fileName.startsWith(`${stock}_part1`)) {
            part1Path = name;
          } else {
            fullPath = name;
          }

          // Parse OHLC from filename if present (e.g., _o11875_h11925_l11375_c11700_p11800)
          if (!parsedOhlc) {
            const ohlcMatch = fileName.match(/_o(\d+)_h(\d+)_l(\d+)_c(\d+)_p(\d+)/);
            if (ohlcMatch) {
              parsedOhlc = {
                open: Number(ohlcMatch[1]),
                high: Number(ohlcMatch[2]),
                low: Number(ohlcMatch[3]),
                close: Number(ohlcMatch[4]),
                prevClose: Number(ohlcMatch[5]),
              };
            }
          }
        }

        if (part1Path || fullPath) {
          return {
            part1ZipUrl: part1Path ? makeUrl(part1Path) : null,
            fullZipUrl: fullPath ? makeUrl(fullPath) : null,
            parsedOhlc,
          };
        }
      }
    }
  } catch (err) {
    console.warn('[boBundleBlob] Failed to list blobs, falling back to simple paths:', err);
  }

  return {
    part1ZipUrl: makeUrl(defaultPart1Path),
    fullZipUrl: makeUrl(defaultFullPath),
    parsedOhlc: null,
  };
}
